import fscreen from 'fscreen';
import { reduxKey, defaultState } from './constants';
import { getCurrentModal } from '../../Modals/redux/selectors';
import { getIsMobile, getViewMode, getPublicSession, } from '../../publicSession/selectors';
import { MeasuredCookieType } from '../../Measured';
import { getCookieSize } from '../../Measured/redux/selectors';
import { DeviationViewMode } from '@wix/da-react-context/pkg/SessionContext';
import ddt from '@wix/da-ddt';
import { getDeviationResourceType, getDeviationType, isLegacyStatus, } from '../../DeviationViews/_helpers';
import { getBiEventComponentByType } from './helpers';
import { DeviationTypes } from '../../types/deviation';
import { getIsDuperbrowseActive } from '../../Duperbrowse/redux/selectors';
import { BlockReasons } from '../../Moderation/redux/types';
import { getHighestPriorityBlockReasonForDeviation, } from '../../Moderation/redux/selectors';
import { getBasicTierSubscriptionInfo, } from '../../utils/hooks/useSubscriptionTier';
import { normalizePremiumFolderData } from '../../utils/normalizePremiumFolderData';
import cssVars from '@wix/da-ds/src/styles/variables.scss';
var logger = ddt.createLogger('devpage');
var isSSR = typeof window === 'undefined';
var FORCED_THEATER_BREAKPOINT = parseInt(cssVars.forceTheaterBreakpoint, 10);
var SIDEBAR_WIDTH = parseInt(cssVars.sidebarWidth, 10);
var DUPER_PADDLE_WIDTH = parseInt(cssVars.duperPaddleWidth, 10);
function getStageSizeFromCookie(state) {
    // get most recent measured cookie for actual stage size
    var cookieSize = getCookieSize(state, MeasuredCookieType.DEVIATION_ARTSTAGE);
    if (!cookieSize || !Array.isArray(cookieSize)) {
        // some arbitrary default size, based on 1280x800 screen,
        // ignoring browser chrome...
        return {
            width: 705,
            height: 680, // 800 (screen) - 50 (header) - 70 (action bar)
        };
    }
    var width = cookieSize[0], height = cookieSize[1];
    return {
        width: width,
        height: height,
    };
}
function getStoreSlice(state) {
    return state[reduxKey] || defaultState;
}
export function getIsZoomed(state) {
    return getStoreSlice(state).viewOptions.isInZoom;
}
export function getIsDuperbrowseAllowed(state) {
    // if any modal is open, we don't want to move
    if (getCurrentModal(state)) {
        return false;
    }
    // zoom vs duper is messy
    if (getIsZoomed(state)) {
        return false;
    }
    return true;
}
export function getVisiblePanel(state, actionBar) {
    if (actionBar === void 0) { actionBar = 'primary'; }
    if (getStoreSlice(state).viewOptions.activeActionBar !== actionBar) {
        return false;
    }
    return getStoreSlice(state).viewOptions.visiblePanel;
}
export function getIsAnyPanelVisible(state) {
    return !!getStoreSlice(state).viewOptions.visiblePanel;
}
function getScreenSize(state) {
    return getStoreSlice(state).viewOptions.screenSize;
}
export function getIsTheaterForced(state, deviation) {
    if (getIsMobile(state)) {
        return true;
    }
    // commission don't have sidebar so they are effectively always theater
    if (deviation && getDeviationType(deviation) === DeviationTypes.COMMISSION) {
        return true;
    }
    var screenSize = getScreenSize(state);
    // fall back to guesstimating from stage cookie size in SSR
    var width = screenSize
        ? screenSize.width
        : Number(getStageSizeFromCookie(state).width) +
            DUPER_PADDLE_WIDTH +
            SIDEBAR_WIDTH;
    // force theater if art stage width + sidebar width < breakpoint
    return width <= FORCED_THEATER_BREAKPOINT;
}
export function getIsFullscreenAllowed(state) {
    if (!isSSR && !fscreen.fullscreenEnabled) {
        return false;
    }
    if (getIsMobile(state)) {
        return false;
    }
    return true;
}
function getIsFullscreen(state) {
    var isBrowserFullscreen = !!fscreen.fullscreenElement;
    var isStateFullscreen = getViewMode(state) === DeviationViewMode.Fullscreen;
    // weird shit happens sometimes
    if (isStateFullscreen !== isBrowserFullscreen) {
        logger.warn('Browser and store fullscreen state have diverged', {
            browser: isBrowserFullscreen,
            store: isStateFullscreen,
        });
    }
    // the browser is our real source of truth, store state is just to force react updates
    return isBrowserFullscreen;
}
function getIsTheater(state, deviation) {
    return (getIsTheaterForced(state, deviation) ||
        getViewMode(state) === DeviationViewMode.Theater);
}
export function getEffectiveViewMode(state, deviation) {
    if (getIsFullscreen(state)) {
        return DeviationViewMode.Fullscreen;
    }
    // special poll only support normal
    if (deviation &&
        (DeviationTypes.POLL === getDeviationType(deviation) ||
            isLegacyStatus(deviation))) {
        return DeviationViewMode.Normal;
    }
    if (getIsTheater(state)) {
        return DeviationViewMode.Theater;
    }
    return DeviationViewMode.Normal;
}
export function getBiContentViewStatus(state, deviation) {
    var _a;
    if (!deviation) {
        return undefined;
    }
    var biBlockReasons = (_a = {},
        _a[BlockReasons.Antisocial] = 'antisocial_logged_out',
        _a[BlockReasons.MatureLoggedOut] = 'mature_logged_out',
        _a[BlockReasons.BlockedByArtist] = 'viewer_blocked',
        _a[BlockReasons.ArtistBlocked] = 'author_blocked',
        _a[BlockReasons.DeviationBlocked] = 'i_just_dont_want_to_see_this',
        _a[BlockReasons.StrictMatureAge] = 'strict_mature_underage',
        _a[BlockReasons.MatureAge] = 'mature_underage',
        _a[BlockReasons.MatureFilter] = 'mature_filter_on',
        _a);
    var blockReason = getHighestPriorityBlockReasonForDeviation(state, deviation);
    if (blockReason && biBlockReasons[blockReason] !== undefined) {
        return biBlockReasons[blockReason];
    }
    var _b = normalizePremiumFolderData(deviation === null || deviation === void 0 ? void 0 : deviation.premiumFolderData), isPremiumLocked = _b.isPremiumLocked, isPaidFolder = _b.isPaidFolder, isWatchToAccess = _b.isWatchToAccess, isPrivilegedFolder = _b.isPrivilegedFolder;
    var deviationType = getDeviationType(deviation);
    if (isPremiumLocked && deviationType !== DeviationTypes.LITERATURE) {
        if (isPaidFolder) {
            return 'pg_locked';
        }
        if (isWatchToAccess) {
            return 'watchers_locked';
        }
        if (isPrivilegedFolder) {
            return 'core_locked';
        }
    }
    var _c = getBasicTierSubscriptionInfo(deviation), needsSubscription = _c.needsSubscription, isSubscribedButBlocked = _c.isSubscribedButBlocked;
    if (needsSubscription || isSubscribedButBlocked) {
        return 'subscription_locked';
    }
}
export function getBiEventData(state, deviation) {
    return {
        view: 'submission',
        publicSession: getPublicSession(state),
        eventComponent: getBiEventComponentByType(getDeviationType(deviation)),
        deviationId: deviation.deviationId,
        deviationResourceType: getDeviationResourceType(deviation),
        isDuperbrowse: getIsDuperbrowseActive(state),
        contentViewStatus: getBiContentViewStatus(state, deviation),
    };
}
//# sourceMappingURL=selectors.js.map